import type { DeviceSearchFilters } from '@/api/deviceApi'
import { buildDeviceSearchFilters } from '@/components/filterable-search-bar/DeviceFiltersUtils'
import { convertTimeRangeToMs } from '@/config/utils'
import { conditionOperator, conditionValues, isRelativeTimeCondition } from './groupedFilterFields'
import type { FilterGroup, FilterState, GroupJoiner } from './types'

/**
 * Relative-time conditions (heartbeat / any Date-typed state column) store a display value
 * ('12hours' / '43200000ms'); the backend compares against an absolute epoch. `in last X` is
 * `heartbeat > now − X`, and its negation `not in last X` is `heartbeat < now − X`.
 *
 * This lives in the builder rather than at a call site so every consumer — the Device Management bar
 * and derived-state conditions alike — converts identically. Without it the raw '12hours' string
 * reaches /search and matches nothing.
 */
const mapTimeRangeCondition = (condition: FilterState): FilterState => {
	const ms = convertTimeRangeToMs(conditionValues(condition)[0] ?? '')
	const negated = conditionOperator(condition) === 'not_in'
	const operator = negated ? '<' : '>'
	return { ...condition, operator, operatorLabel: operator, value: [ms], columnType: 'DateTime', type: 'state' }
}

/** Returns a node for a condition the default builder can't map, or `undefined` to defer to it. */
export type ResolveCondition = (condition: FilterState) => DeviceSearchFilters | null | undefined

/** Operators the client-side matcher answers `true` for on a missing value; every other one answers `false`. */
const NULL_MATCHES_OPERATORS = new Set(['!=', 'not=', 'ne', 'is_not', 'not_in', 'notEquals'])

/** Already exact on a missing value — guarding one of these would contradict it into matching nothing. */
const NULLNESS_OPERATORS = new Set(['is_null', 'is_not_null'])

const nullnessLeaf = (condition: FilterState, isNull: boolean): DeviceSearchFilters | null =>
	buildDeviceSearchFilters([
		{
			...condition,
			operator: isNull ? 'is_null' : 'is_not_null',
			operatorLabel: isNull ? 'is_null' : 'is_not_null',
			isNullOperator: true,
			value: [],
		},
	])

/**
 * Pin a leaf to the answer `matchesCondition` gives it when the column holds no value.
 *
 * ClickHouse compares a missing column to NULL rather than to false, and `NOT NULL` is NULL — so a
 * device reporting nothing silently drops out of any negated filter, however the client badges it.
 * `NULL AND false` is false and `NULL OR true` is true, which is exactly the coercion each side needs.
 */
const withNullSafety = (condition: FilterState, node: DeviceSearchFilters): DeviceSearchFilters => {
	const operator = condition.operatorLabel || condition.operator
	// `isNullOperator` and the operator itself are set together by every builder here, but a guard placed
	// on a nullness leaf would AND `is_null` with `is_not_null`, so read both rather than trust one.
	if (condition.isNullOperator || NULLNESS_OPERATORS.has(operator) || NULLNESS_OPERATORS.has(condition.operator)) {
		return node
	}
	const nullMatches = NULL_MATCHES_OPERATORS.has(operator)
	const guard = nullnessLeaf(condition, nullMatches)
	if (!guard) {
		return node
	}
	return nullMatches ? { or: [node, guard] } : { and: [node, guard] }
}

export interface GroupedFilterBuildOptions {
	/**
	 * Make every leaf NULL-free, so the result stays exact under an enclosing `not`. Costs one extra
	 * nullness leaf per condition, so it is opt-in rather than the default.
	 */
	nullSafe?: boolean
}

// Backend rejects an and/or node with more than 16 args; nest into groups of ≤16 (both are associative).
const MAX_FILTER_GROUP_SIZE = 16

/** Constructs a backend node's `and`/`or` wrapper — the one thing that differs between node types
 *  (`DeviceSearchFilters`, `StreamSearchFilters`, …), which otherwise share this exact and/or/associativity shape. */
export interface GroupedFilterNodeOps<TNode> {
	and: (parts: TNode[]) => TNode
	or: (parts: TNode[]) => TNode
}

function joinNodes<TNode>(op: GroupJoiner, parts: TNode[], ops: GroupedFilterNodeOps<TNode>): TNode {
	if (parts.length === 1) {
		return parts[0]
	}
	if (parts.length <= MAX_FILTER_GROUP_SIZE) {
		return op === 'or' ? ops.or(parts) : ops.and(parts)
	}
	const chunks: TNode[] = []
	for (let i = 0; i < parts.length; i += MAX_FILTER_GROUP_SIZE) {
		chunks.push(joinNodes(op, parts.slice(i, i + MAX_FILTER_GROUP_SIZE), ops))
	}
	return joinNodes(op, chunks, ops)
}

function buildGroupNode<TNode>(
	group: FilterGroup,
	buildCondition: (condition: FilterState) => TNode | null,
	ops: GroupedFilterNodeOps<TNode>,
): TNode | null {
	const nodes = group.conditions.map(buildCondition).filter((node): node is TNode => node !== null)

	if (nodes.length === 0) {
		return null
	}
	return joinNodes(group.operator, nodes, ops)
}

/**
 * Compose `FilterGroup[]` into a backend recursive filter node, generic over the node type — device and
 * stream search filters (and any future one) share this exact and/or/associativity shape, only the
 * per-condition mapping and the `and`/`or` wrapper differ.
 *
 * Groups are folded left-to-right, coalescing consecutive same-joiner boundaries into a single flat
 * and/or array. The uniform all-OR case (the default) yields `{ or: [G1, G2, …] }`. Mixed joiners fold
 * left-associatively: `G1 OR G2 AND G3` → `(G1 OR G2) AND G3`. Empty groups (and their boundary) are
 * dropped; returns null when nothing survives.
 */
export function buildGroupedSearchFilters<TNode>(
	groups: FilterGroup[],
	buildCondition: (condition: FilterState) => TNode | null,
	ops: GroupedFilterNodeOps<TNode>,
): TNode | null {
	const built = groups
		.map(group => ({ node: buildGroupNode(group, buildCondition, ops), joiner: group.joiner }))
		.filter((entry): entry is { node: TNode; joiner: GroupJoiner } => entry.node !== null)

	if (built.length === 0) {
		return null
	}

	let acc = built[0].node
	let i = 1
	while (i < built.length) {
		const op = built[i].joiner
		const run: TNode[] = [acc]
		while (i < built.length && built[i].joiner === op) {
			run.push(built[i].node)
			i++
		}
		acc = joinNodes(op, run, ops)
	}
	return acc
}

const deviceSearchFiltersOps: GroupedFilterNodeOps<DeviceSearchFilters> = {
	and: parts => ({ and: parts }),
	or: parts => ({ or: parts }),
}

/**
 * Convert one group's conditions into a single backend node via the existing `buildDeviceSearchFilters`
 * (single-element list → single node, so heartbeat/regex/coercion all come for free), then join by the
 * group's operator (default AND) — see `buildGroupedSearchFilters`.
 */
export const buildGroupedDeviceSearchFilters = (
	groups: FilterGroup[],
	resolveCondition?: ResolveCondition,
	options?: GroupedFilterBuildOptions,
): DeviceSearchFilters | null =>
	buildGroupedSearchFilters(
		groups,
		condition => {
			// A resolver owns the condition outright when it returns anything but `undefined` — it stands in
			// for fields that have no column of their own (a derived column expands to its own conditions).
			const resolved = resolveCondition?.(condition)
			if (resolved !== undefined) {
				return resolved
			}
			// Guarding the mapped condition, not the raw one: 'not in last X' maps to `heartbeat < t`, which
			// the matcher answers false for on a missing heartbeat like any other comparison.
			const mapped = isRelativeTimeCondition(condition) ? mapTimeRangeCondition(condition) : condition
			const node = buildDeviceSearchFilters([mapped])
			if (node === null || !options?.nullSafe) {
				return node
			}
			return withNullSafety(mapped, node)
		},
		deviceSearchFiltersOps,
	)

/**
 * AND a host's fixed scope under the query the bar built, for a list that must stay inside a set the
 * user did not choose (a device dashboard's preset devices).
 *
 * Deliberately not the same-field union `combineFilters` performs for the device dropdown. There the
 * two sides are independent value sets; here the scope is what the user searches *within*, so a
 * condition on a field the scope also constrains has to narrow rather than widen. Merging them instead
 * is what let a picked device escape a dashboard's preset.
 */
export function withScopeQuery<TQuery>(scope: TQuery | null | undefined, query: TQuery | null): TQuery | null {
	if (scope === null || scope === undefined) {
		return query
	}
	if (query === null) {
		return scope
	}
	return { and: [scope, query] } as TQuery
}
